import { HStack, Icon, VStack } from "@/shared/ui/primitives";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_PADDING = 8;
const MAX_MENU_HEIGHT = 280;

type MenuRect = {
	top: number;
	left: number;
	width: number;
	maxHeight: number;
};

export const calculateSelectMenuRect = (
	rect: Pick<DOMRect, "top" | "left" | "bottom" | "width">,
	viewport: { width: number; height: number },
): MenuRect => {
	const width = Math.min(rect.width, viewport.width - VIEWPORT_PADDING * 2);
	const left = Math.max(
		VIEWPORT_PADDING,
		Math.min(rect.left, viewport.width - VIEWPORT_PADDING - width),
	);

	const spaceBelow = Math.max(viewport.height - rect.bottom - VIEWPORT_PADDING, 0);
	const spaceAbove = Math.max(rect.top - VIEWPORT_PADDING, 0);
	const shouldOpenAbove = spaceBelow < 120 && spaceAbove > spaceBelow;
	const availableHeight = shouldOpenAbove ? spaceAbove : spaceBelow;
	const maxHeight = Math.max(120, Math.min(MAX_MENU_HEIGHT, Math.floor(availableHeight)));

	const idealTop = shouldOpenAbove ? rect.top - maxHeight : rect.bottom;
	const top = Math.max(
		VIEWPORT_PADDING,
		Math.min(idealTop, viewport.height - VIEWPORT_PADDING - maxHeight),
	);

	return {
		top,
		left,
		width,
		maxHeight,
	};
};

export interface SelectInputFieldOption {
	label: string;
	value: string;
}

export interface SelectInputFieldProps {
	label: string;
	value: string;
	options: SelectInputFieldOption[];
	onChange: (value: string) => void;
	errorMessage?: string;
}

export const SelectInputField = ({
	label,
	value,
	options,
	onChange,
	errorMessage,
}: SelectInputFieldProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const [menuRect, setMenuRect] = useState<MenuRect | null>(null);

	const selectedOption = useMemo(() => {
		return options.find((option) => option.value === value) ?? options[0] ?? null;
	}, [options, value]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const updateMenuRect = () => {
			const trigger = triggerRef.current;
			if (!trigger) {
				return;
			}

			const rect = trigger.getBoundingClientRect();
			setMenuRect(
				calculateSelectMenuRect(rect, {
					width: window.innerWidth,
					height: window.innerHeight,
				}),
			);
		};

		updateMenuRect();

		const handlePointerDown = (event: PointerEvent) => {
			const target = event.target as Node | null;
			if (!target) {
				return;
			}

			if (rootRef.current?.contains(target)) {
				return;
			}

			if (menuRef.current?.contains(target)) {
				return;
			}

			setIsOpen(false);
		};

		const handleWindowChange = () => {
			updateMenuRect();
		};

		document.addEventListener("pointerdown", handlePointerDown, true);
		window.addEventListener("resize", handleWindowChange);
		window.addEventListener("scroll", handleWindowChange, true);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown, true);
			window.removeEventListener("resize", handleWindowChange);
			window.removeEventListener("scroll", handleWindowChange, true);
		};
	}, [isOpen]);

	return (
		<div className="w-full" ref={rootRef}>
			<VStack className="w-full">
				<HStack className="w-full justify-between text-sm text-ct-strong font-medium p-[6px]">
					{label}
				</HStack>

				<div className="relative w-full">
					<button
						type="button"
						ref={triggerRef}
						className={`w-full p-[14px] pr-[42px] rounded-[10px] bg-bg-regular text-ct-strong text-sm font-normal outline-none ${errorMessage ? "outline-dg-strong" : "outline-outline"}`}
						onClick={() => setIsOpen((current) => !current)}
					>
						<span className="w-full">{selectedOption?.label ?? "Select"}</span>
						<Icon
							name="Down"
							width={18}
							height={18}
							className={`pointer-events-none absolute right-[14px] top-1/2 -translate-y-1/2 text-ct-regular transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
						/>
					</button>

					{isOpen && menuRect
						? createPortal(
								<div
									ref={menuRef}
									className="fixed z-[9999] flex flex-col justify-start items-center max-h-[240px] overflow-auto rounded-[10px] bg-bg-weak outline-outline p-[6px] gap-[4px] shadow-[0_8px_24px_rgba(0,0,0,0.14)]"
									style={{
										top: menuRect.top,
										left: menuRect.left,
										width: menuRect.width,
										maxHeight: menuRect.maxHeight,
									}}
								>
									{options.map((option) => {
										const selected = option.value === value;

										return (
											<button
												type="button"
												key={option.value}
												className={`w-full px-[10px] py-[8px] rounded-[8px] text-sm ${selected ? "bg-bg-regular text-ct-strong" : "text-ct-regular hover:bg-bg-regular hover:text-ct-strong"}`}
												onClick={() => {
													onChange(option.value);
													setIsOpen(false);
												}}
											>
												<span className="w-full">{option.label}</span>
											</button>
										);
									})}
								</div>,
								document.body,
							)
						: null}
				</div>

				{errorMessage ? (
					<span className="w-full px-[6px] py-[4px] text-sm text-dg-strong font-normal">
						{errorMessage}
					</span>
				) : null}
			</VStack>
		</div>
	);
};
